import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Download, FileSpreadsheet } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function EntriesSheet() {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchEntries = async () => {
      try {
        const endpoint = user?.role === "admin" ? "/job-entries/all" : "/job-entries";
        const response = await axios.get(`${API_URL}${endpoint}`, {
          headers: getAuthHeader(),
        });
        setEntries(response.data);
      } catch (error) {
        console.error("Failed to fetch entries:", error);
        toast.error("Failed to load entries");
      } finally {
        setIsLoading(false);
      }
    };
    fetchEntries();
  }, [user, getAuthHeader]);

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const exportToCSV = () => {
    const headers = ["Date", "Time", "User Email", "User Name", "Hours", "Materials", "Permit Fee", "Subcontractor", "Price", "Notes"];
    const rows = entries.map(entry => [
      formatDate(entry.created_at),
      formatTime(entry.created_at),
      entry.user_email,
      entry.user_name,
      entry.labor_hours,
      entry.materials_cost,
      entry.permit_fee || 0,
      entry.subcontractor_cost || 0,
      entry.customer_price,
      `"${entry.technician_notes.replace(/"/g, '""')}"`
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `bluebear_entries_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV exported!");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary font-oswald text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              className="text-slate-600"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-oswald font-bold text-[#003366] text-xl tracking-tight flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                ENTRIES SHEET
              </h1>
              <p className="text-xs text-slate-500">{entries.length} total entries</p>
            </div>
          </div>
          <Button
            onClick={exportToCSV}
            className="bg-[#003366] hover:bg-[#002244]"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-oswald text-[#003366] text-lg">
              All Job Entries
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {entries.length === 0 ? (
              <div className="p-12 text-center">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="font-oswald text-xl text-slate-600 mb-2">No Entries Yet</h3>
                <p className="text-slate-500">Job entries will appear here after you save them.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-bold text-[#003366] whitespace-nowrap">Date</TableHead>
                      <TableHead className="font-bold text-[#003366] whitespace-nowrap">Time</TableHead>
                      <TableHead className="font-bold text-[#003366] whitespace-nowrap">User Email</TableHead>
                      <TableHead className="font-bold text-[#003366] whitespace-nowrap">Name</TableHead>
                      <TableHead className="font-bold text-[#003366] whitespace-nowrap text-right">Hours</TableHead>
                      <TableHead className="font-bold text-[#003366] whitespace-nowrap text-right">Materials</TableHead>
                      <TableHead className="font-bold text-[#003366] whitespace-nowrap text-right">Permit</TableHead>
                      <TableHead className="font-bold text-[#003366] whitespace-nowrap text-right">Subcontractor</TableHead>
                      <TableHead className="font-bold text-[#003366] whitespace-nowrap text-right">Price</TableHead>
                      <TableHead className="font-bold text-[#003366] whitespace-nowrap">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry, index) => (
                      <TableRow 
                        key={entry.id} 
                        className={index % 2 === 0 ? "bg-white" : "bg-slate-50/50"}
                        data-testid={`entry-row-${entry.id}`}
                      >
                        <TableCell className="whitespace-nowrap">{formatDate(entry.created_at)}</TableCell>
                        <TableCell className="whitespace-nowrap">{formatTime(entry.created_at)}</TableCell>
                        <TableCell className="whitespace-nowrap font-medium">{entry.user_email}</TableCell>
                        <TableCell className="whitespace-nowrap">{entry.user_name}</TableCell>
                        <TableCell className="text-right tabular-nums">{entry.labor_hours}h</TableCell>
                        <TableCell className="text-right tabular-nums">${entry.materials_cost.toFixed(2)}</TableCell>
                        <TableCell className="text-right tabular-nums">${(entry.permit_fee || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right tabular-nums">${(entry.subcontractor_cost || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right tabular-nums font-bold text-[#003366]">${entry.customer_price.toLocaleString()}</TableCell>
                        <TableCell className="max-w-xs truncate" title={entry.technician_notes}>
                          {entry.technician_notes}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Stats */}
        {entries.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Entries</p>
                <p className="font-oswald text-3xl font-bold text-[#003366]">{entries.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Revenue</p>
                <p className="font-oswald text-3xl font-bold text-[#003366]">
                  ${entries.reduce((sum, e) => sum + e.customer_price, 0).toLocaleString()}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Hours</p>
                <p className="font-oswald text-3xl font-bold text-[#003366]">
                  {entries.reduce((sum, e) => sum + e.labor_hours, 0).toFixed(1)}h
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Avg Price</p>
                <p className="font-oswald text-3xl font-bold text-[#003366]">
                  ${Math.round(entries.reduce((sum, e) => sum + e.customer_price, 0) / entries.length).toLocaleString()}
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
